import { Show, For, createMemo, createSignal, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { usePanel } from "@/shared/use-panel";
import { useJobEvents } from "@/shared/use-job-events";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";

interface SoftwareRow {
  displayName: string;
  displayVersion?: string | null;
  publisher?: string | null;
  installDate?: string | null;
  installLocation?: string | null;
  estimatedSizeMB?: number | null;
  productCode: string;
  uninstallString?: string | null;
  quietUninstallString?: string | null;
  systemComponent?: boolean | null;
  is64bit?: boolean | null;
}

interface ListResult {
  rows: SoftwareRow[];
  count?: number;
  truncatedAt?: number;
}

function formatInstallDate(raw: string | null | undefined): string {
  if (!raw) return "";
  // Format Add/Remove Programs : "YYYYMMDD" — convertir en "YYYY-MM-DD"
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw;
}

function formatSize(mb: number | null | undefined): string {
  if (mb == null || mb <= 0) return "";
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function archLabel(is64: boolean | null | undefined): string {
  if (is64 === true) return "x64";
  if (is64 === false) return "x86";
  return "";
}

const SoftwarePanel: Component = () => {
  const ws = useWs();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const [filter, setFilter] = createSignal("");
  const [publisherFilter, setPublisherFilter] = createSignal("All");
  const [activeJobId, setActiveJobId] = createSignal<string | null>(null);
  const [uninstallingProductCode, setUninstallingProductCode] = createSignal<string | null>(null);

  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "software_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const panel = usePanel(() => ({ isFetching: listQuery.isFetching, error: listQuery.error }));
  const jobState = useJobEvents(() => activeJobId());

  // Liste unique des éditeurs pour le filtre
  const publishers = createMemo(() => {
    const all = listQuery.data?.rows ?? [];
    const set = new Set<string>();
    for (const r of all) {
      const p = (r.publisher ?? "").trim();
      if (p) set.add(p);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  });

  const rows = createMemo(() => {
    const all = listQuery.data?.rows ?? [];
    const q = filter().trim().toLowerCase();
    const pub = publisherFilter();
    return all.filter((r) => {
      if (pub !== "All" && (r.publisher ?? "") !== pub) return false;
      if (!q) return true;
      return (
        (r.displayName ?? "").toLowerCase().includes(q) ||
        (r.publisher ?? "").toLowerCase().includes(q) ||
        (r.displayVersion ?? "").toLowerCase().includes(q) ||
        (r.productCode ?? "").toLowerCase().includes(q)
      );
    });
  });

  async function uninstall(row: SoftwareRow) {
    const ok = await panel.confirm.confirm({
      title: `Désinstaller — ${row.displayName}`,
      body: `Lancer la désinstallation silencieuse de "${row.displayName}" (${row.displayVersion ?? "?"}) ?\nL'opération peut prendre plusieurs minutes.`,
      level: "danger",
      confirmLabel: "Désinstaller",
      entityName: row.displayName,
    });
    if (!ok) return;

    setUninstallingProductCode(row.productCode);
    panel.setActionStatus({ text: `Désinstallation de "${row.displayName}" en cours…`, level: "info" });
    try {
      const res = await panel.tools.call<{ jobId: string; async: boolean }>(
        "software_uninstall",
        {
          productCode: row.productCode,
          displayName: row.displayName,
          silent: true,
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      if (res?.jobId) {
        setActiveJobId(res.jobId);
      } else {
        // Réponse synchrone : rafraîchir directement
        setUninstallingProductCode(null);
        panel.setActionStatus({ text: `Désinstallation terminée.`, level: "info" });
        await listQuery.refetch();
        setTimeout(() => panel.setActionStatus(null), 2000);
      }
    } catch (err) {
      setUninstallingProductCode(null);
      panel.setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  // Watcher pour détecter la fin du job de désinstallation
  let prevJobDone = false;
  function checkJobDone(): boolean {
    const js = jobState();
    const done = js?.status === "done" || js?.status === "error";
    if (done && !prevJobDone) {
      prevJobDone = true;
      const isError = js?.status === "error";
      setTimeout(async () => {
        await listQuery.refetch();
        setActiveJobId(null);
        setUninstallingProductCode(null);
        panel.setActionStatus(
          isError
            ? { text: `Erreur désinstallation: ${js?.error ?? "inconnue"}`, level: "danger" }
            : { text: "Désinstallation terminée.", level: "info" },
        );
        setTimeout(() => panel.setActionStatus(null), 3000);
        prevJobDone = false;
      }, 800);
    } else if (!done) {
      prevJobDone = false;
    }
    return done;
  }

  const columns: Column<SoftwareRow>[] = [
    { accessorKey: "displayName", header: "Nom" },
    {
      accessorKey: "displayVersion",
      header: "Version",
      cell: (info) => String(info.getValue() ?? ""),
    },
    {
      accessorKey: "publisher",
      header: "Éditeur",
      cell: (info) => String(info.getValue() ?? ""),
    },
    {
      accessorKey: "installDate",
      header: "Date installation",
      cell: (info) => formatInstallDate(info.getValue() as string | null | undefined),
    },
    {
      accessorKey: "estimatedSizeMB",
      header: "Taille",
      cell: (info) => formatSize(info.getValue() as number | null | undefined),
    },
    {
      accessorKey: "is64bit",
      header: "Arch.",
      cell: (info) => archLabel(info.getValue() as boolean | null | undefined),
    },
  ];

  const actions = (
    <>
      <input
        class="panel-filter-input"
        placeholder="Filtrer (nom, version, éditeur)…"
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <select
        class="panel-select"
        value={publisherFilter()}
        onChange={(e) => setPublisherFilter(e.currentTarget.value)}
      >
        <option value="All">Tous éditeurs</option>
        <For each={publishers()}>{(p) => <option value={p}>{p}</option>}</For>
      </select>
      <button class="btn btn--xs btn--ghost" onClick={() => listQuery.refetch()} title="Rafraîchir">
        ↺ Rafraîchir
      </button>
    </>
  );

  return (
    <PanelChrome icon="📦" title="Logiciels installés" tagType="core" actions={actions}>
      <PanelLayout title="Logiciels installés" status={panel.status()}>
        <DataTable<SoftwareRow>
          rows={rows()}
          columns={columns}
          rowActions={(r) => {
            const isUninstalling = uninstallingProductCode() === r.productCode;
            return (
              <button
                class="btn btn--xs btn--danger"
                disabled={isUninstalling || !!uninstallingProductCode()}
                onClick={() => uninstall(r)}
                title={isUninstalling ? "Désinstallation en cours…" : "Désinstaller silencieusement"}
              >
                {isUninstalling ? "Désinstallation…" : "Désinstaller"}
              </button>
            );
          }}
        />

        <Show when={jobState()}>
          {(js) => {
            checkJobDone();
            return (
              <div class="panel-detail-section" style={{ "margin-top": "8px" }}>
                <h4>
                  Désinstallation{" "}
                  <PanelBadge intent={js().status === "done" ? "ok" : js().status === "error" ? "danger" : "info"}>
                    {js().status}
                  </PanelBadge>
                </h4>
                <pre class="panel-job-log">
                  <For each={js().logs}>{(line) => <div class="panel-job-log-line">{line}</div>}</For>
                </pre>
                <Show when={js().error}>
                  <div class="panel-job-log-line" style={{ color: "var(--danger, #d33)" }}>
                    {js().error}
                  </div>
                </Show>
              </div>
            );
          }}
        </Show>

        <div
          class="panel-data-empty"
          style={{
            "margin-top": "8px",
            "font-size": "11px",
            opacity: "0.7",
            "text-align": "right",
            padding: "4px 8px",
          }}
        >
          <Show when={listQuery.data} fallback={enabled() ? "Chargement…" : ""}>
            {rows().length} logiciel{rows().length > 1 ? "s" : ""} affiché{rows().length > 1 ? "s" : ""}
            {listQuery.data?.count != null && rows().length !== listQuery.data.count && (
              <span>
                {" "}
                sur {listQuery.data.count} installé{listQuery.data.count > 1 ? "s" : ""}
              </span>
            )}
            {listQuery.data?.count == null
              ? null
              : rows().length === listQuery.data.count && (
                  <span>
                    {" "}
                    · {listQuery.data.count} installé{listQuery.data.count > 1 ? "s" : ""}
                  </span>
                )}
          </Show>
        </div>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("software", SoftwarePanel);
export default SoftwarePanel;
